import { Fragment, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  AlertCircle,
  ArrowLeft,
  Clock,
  History,
  MessageSquare,
  Pin,
  PinOff,
  Plus,
  RotateCcw,
  Search,
  SearchX,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "../../ui/utils";
import { searchConversations } from "../lib/historySearch";
import { startOfLocalDay } from "../lib/transcript";
import type { ConversationSummary, HistoryContext } from "../types";

export const HISTORY_WINDOW_DAYS = 15;

const DAY_MS = 86_400_000;
const SEARCH_MAX_CHARS = 100;

export function relativeTime(ts: number, now: number): string {
  const diff = Math.max(0, now - ts);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export type ConversationRowData = ConversationSummary & { lastMessageMs: number };

export interface HistoryGroup {
  label: string;
  items: ConversationRowData[];
}

// Null lastMessageAt rows are never-used shells AND sort to the top in Postgres, so filter them.
export function withinWindow(conversations: ConversationSummary[], now: number): ConversationRowData[] {
  const today = startOfLocalDay(now);
  const rows: ConversationRowData[] = [];
  for (const c of conversations) {
    if (!c.lastMessageAt) continue;
    const ms = Date.parse(c.lastMessageAt);
    if (Number.isNaN(ms)) continue;
    const daysAgo = Math.max(0, Math.round((today - startOfLocalDay(ms)) / DAY_MS));
    if (daysAgo >= HISTORY_WINDOW_DAYS) continue;
    rows.push({ ...c, lastMessageMs: ms });
  }
  return rows.sort((a, z) => z.lastMessageMs - a.lastMessageMs);
}

export function groupConversations(
  rows: ConversationRowData[],
  pinnedIds: string[],
  now: number,
): HistoryGroup[] {
  const today = startOfLocalDay(now);
  const pinned = new Set(pinnedIds);

  const groups: HistoryGroup[] = [
    { label: "Pinned", items: [] },
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "Previous 7 days", items: [] },
    { label: `Previous ${HISTORY_WINDOW_DAYS} days`, items: [] },
  ];

  for (const row of rows) {
    if (pinned.has(row.id)) {
      groups[0].items.push(row);
      continue;
    }
    const daysAgo = Math.max(0, Math.round((today - startOfLocalDay(row.lastMessageMs)) / DAY_MS));
    if (daysAgo === 0) groups[1].items.push(row);
    else if (daysAgo === 1) groups[2].items.push(row);
    else if (daysAgo < 7) groups[3].items.push(row);
    else groups[4].items.push(row);
  }

  return groups.filter((g) => g.items.length > 0);
}

export function HistoryBanner({
  context,
  onNewChat,
}: {
  context: HistoryContext;
  onNewChat: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const when = context.lastMessageAt ? relativeTime(Date.parse(context.lastMessageAt), Date.now()) : null;

  return (
    <motion.div
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="flex shrink-0 items-center gap-2 border-b border-violet-500/15 bg-violet-500/[0.07] px-3 py-2 dark:border-violet-300/15 dark:bg-violet-400/[0.08] sm:px-4"
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
        <History className="size-3.5" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.6875rem] font-semibold tracking-wide text-primary">
          Viewing a conversation from history
        </span>
        <span className="block break-words text-[0.6875rem] leading-tight text-muted-foreground">
          {context.title?.trim() || "Untitled conversation"}
          {when ? ` - last active ${when}` : ""}
        </span>
      </span>
      <button
        type="button"
        onClick={onNewChat}
        title="Start a new chat"
        className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg border border-border/70 bg-card px-2 py-1.5 text-[0.6875rem] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-white/10 dark:bg-white/[0.07] dark:hover:bg-white/[0.12]"
      >
        <Plus className="size-3" strokeWidth={2.5} />
        New chat
      </button>
    </motion.div>
  );
}

function Highlight({ text, ranges }: { text: string; ranges: Array<[number, number]> }) {
  if (ranges.length === 0) return <>{text}</>;
  const out: React.ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], i) => {
    if (start > cursor) out.push(<Fragment key={`t${i}`}>{text.slice(cursor, start)}</Fragment>);
    out.push(
      <mark key={`m${i}`} className="rounded-[3px] bg-primary/20 px-0 text-inherit dark:bg-primary/30">
        {text.slice(start, end)}
      </mark>,
    );
    cursor = end;
  });
  if (cursor < text.length) out.push(<Fragment key="tail">{text.slice(cursor)}</Fragment>);
  return <>{out}</>;
}

function PanelShell({
  count,
  onClose,
  toolbar,
  children,
}: {
  count: number;
  onClose: () => void;
  toolbar: React.ReactNode;
  children: React.ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -18 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -18 }}
      transition={{ type: "tween", ease: [0.4, 0, 0.2, 1], duration: 0.22 }}
      role="dialog"
      aria-label="Conversation history"
      className="absolute inset-0 z-20 flex flex-col overflow-hidden bg-white dark:bg-gradient-to-b dark:from-[#211c42] dark:via-[#171430] dark:to-[#110f22]"
    >
      {/* min-h is mirrored from ChatPanel so the bar does not change height. */}
      <div className="relative flex min-h-[3.75rem] shrink-0 items-center justify-between gap-2.5 border-b border-white/20 bg-gradient-to-br from-indigo-600 via-violet-600 to-violet-500 px-4 py-3 dark:border-white/10 dark:from-indigo-600/85 dark:via-violet-600/80 dark:to-fuchsia-600/65">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
        />
        <motion.button
          type="button"
          onClick={onClose}
          whileTap={reduceMotion ? undefined : { scale: 0.9 }}
          title="Back to chat"
          aria-label="Back to chat"
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-colors duration-150 hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:size-9"
        >
          <ArrowLeft className="size-4" />
        </motion.button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[0.875rem] font-bold leading-tight text-white">Chat history</p>
          <p className="truncate text-[0.6875rem] leading-tight text-white/70">
            Last {HISTORY_WINDOW_DAYS} days
            {count > 0 ? ` - ${count} ${count === 1 ? "conversation" : "conversations"}` : ""}
          </p>
        </div>
      </div>

      {toolbar}

      <div className="scrollbar-neutral min-h-0 flex-1 overflow-y-auto [overscroll-behavior:contain]">
        {children}
      </div>
    </motion.div>
  );
}

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="shrink-0 border-b border-border/60 px-3 py-2 dark:border-white/10">
      <div className="relative flex items-center">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 size-3.5 text-muted-foreground"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && value) {
              e.stopPropagation();
              onChange("");
            }
          }}
          maxLength={SEARCH_MAX_CHARS}
          aria-label="Search conversations"
          placeholder="Search"
          className="h-9 w-full rounded-xl border border-border/70 bg-card/60 pl-9 pr-9 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50 focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-white/[0.05] dark:focus:border-violet-300/40 sm:text-[0.8125rem]"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            title="Clear search"
            aria-label="Clear search"
            className="absolute right-1.5 flex size-7 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function CenteredState({
  icon: Icon,
  title,
  body,
  children,
}: {
  icon: typeof Clock;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
      <span className="flex size-11 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/15">
        <Icon className="size-5" strokeWidth={2} />
      </span>
      <p className="mt-3 text-[0.8125rem] font-semibold text-foreground">{title}</p>
      <p className="mt-1 max-w-[17rem] break-words text-[0.75rem] leading-relaxed text-muted-foreground">
        {body}
      </p>
      {children}
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-2 px-3 py-4" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-xl border border-border/60 bg-card/60 px-3 py-3 dark:border-white/10 dark:bg-white/[0.04]"
        >
          <div className="h-3 w-2/3 rounded bg-muted-foreground/20" />
          <div className="mt-2 h-2.5 w-1/4 rounded bg-muted-foreground/15" />
        </div>
      ))}
    </div>
  );
}

// Sibling of the row button, never a child: a button inside a button breaks keyboard activation.
function RowAction({
  label,
  onClick,
  danger = false,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "flex size-8 cursor-pointer items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:size-7",
        danger
          ? "text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
          : "text-muted-foreground hover:bg-accent hover:text-foreground dark:hover:bg-white/[0.12]",
      )}
    >
      {children}
    </button>
  );
}

function ConversationRow({
  conversation,
  now,
  isActive,
  isPinned,
  ranges,
  onSelect,
  onTogglePin,
  onDelete,
}: {
  conversation: ConversationRowData;
  now: number;
  isActive: boolean;
  isPinned: boolean;
  ranges: Array<[number, number]>;
  onSelect: (id: string) => void;
  onTogglePin: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const reduceMotion = useReducedMotion();
  const [confirming, setConfirming] = useState(false);
  const title = conversation.title?.trim() || "Untitled conversation";

  const variants = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 6 },
        show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] as const } },
      };

  if (confirming) {
    return (
      <motion.li variants={variants}>
        <div
          onKeyDown={(e) => {
            if (e.key === "Escape") setConfirming(false);
          }}
          className="flex min-h-[3.25rem] items-center gap-2 rounded-xl border border-red-400/40 bg-red-500/[0.06] px-3 py-2 dark:border-red-400/30 sm:min-h-0"
        >
          <span className="min-w-0 flex-1 text-[0.75rem] leading-snug text-foreground">
            Delete this conversation?
          </span>
          <button
            type="button"
            autoFocus
            onClick={() => setConfirming(false)}
            className="shrink-0 cursor-pointer rounded-lg border border-border/70 bg-card px-2.5 py-1.5 text-[0.6875rem] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-white/10 dark:bg-white/[0.07]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onDelete(conversation.id)}
            className="shrink-0 cursor-pointer rounded-lg bg-red-500 px-2.5 py-1.5 text-[0.6875rem] font-medium text-white transition-colors hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
          >
            Delete
          </button>
        </div>
      </motion.li>
    );
  }

  return (
    <motion.li variants={variants} className="group relative">
      <motion.button
        type="button"
        onClick={() => onSelect(conversation.id)}
        aria-current={isActive ? "true" : undefined}
        aria-label={`Open conversation: ${title}`}
        whileHover={reduceMotion ? undefined : { x: 3 }}
        whileTap={reduceMotion ? undefined : { scale: 0.985 }}
        transition={{ type: "spring", stiffness: 420, damping: 30 }}
        className={cn(
          "flex min-h-[3.25rem] w-full cursor-pointer items-center gap-2.5 rounded-xl border py-2.5 pl-3 pr-32 text-left transition-colors sm:min-h-0 sm:py-2 sm:pr-[7.5rem]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          isActive
            ? "border-primary/40 bg-primary/[0.07] dark:border-violet-300/30 dark:bg-white/[0.08]"
            : "border-border/60 bg-card/60 hover:border-primary/25 hover:bg-accent dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]",
        )}
      >
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-lg transition-colors",
            isPinned
              ? "bg-primary/15 text-primary"
              : isActive
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary dark:bg-white/[0.06]",
          )}
        >
          {isPinned ? (
            <Pin className="size-3.5 fill-current" strokeWidth={2} />
          ) : (
            <MessageSquare className="size-3.5" strokeWidth={2} />
          )}
        </span>
        {/* Titles wrap rather than truncate (CLAUDE.md forbids ellipsis in new UI). */}
        <span className="min-w-0 flex-1 break-words text-[0.8125rem] font-medium leading-snug text-foreground">
          <Highlight text={title} ranges={ranges} />
        </span>
      </motion.button>

      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center gap-1.5">
        <span className="shrink-0 text-[0.6875rem] tabular-nums text-muted-foreground">
          {relativeTime(conversation.lastMessageMs, now)}
        </span>
        <div className="pointer-events-auto flex items-center gap-0.5 opacity-100 transition-opacity duration-150 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 motion-reduce:transition-none">
          <RowAction
            label={isPinned ? "Unpin conversation" : "Pin conversation"}
            onClick={() => onTogglePin(conversation.id)}
          >
            {isPinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
          </RowAction>
          <RowAction label="Delete conversation" danger onClick={() => setConfirming(true)}>
            <Trash2 className="size-3.5" />
          </RowAction>
        </div>
      </div>
    </motion.li>
  );
}

export interface HistoryPanelProps {
  conversations: ConversationSummary[];
  pinnedIds?: string[];
  loading?: boolean;
  error?: string | null;
  actionError?: string | null;
  onDismissActionError?: () => void;
  activeId?: string | null;
  onSelect: (id: string) => void;
  onTogglePin: (id: string) => void;
  onDelete: (id: string) => void;
  onRetry?: () => void;
  onClose: () => void;
}

export function HistoryPanel({
  conversations,
  pinnedIds = [],
  loading = false,
  error = null,
  actionError = null,
  onDismissActionError,
  activeId = null,
  onSelect,
  onTogglePin,
  onDelete,
  onRetry,
  onClose,
}: HistoryPanelProps) {
  const reduceMotion = useReducedMotion();
  const [query, setQuery] = useState("");
  const now = Date.now();

  const rows = useMemo(() => withinWindow(conversations, now), [conversations, now]);
  const searching = query.trim().length > 0;

  const { groups, highlights } = useMemo(() => {
    if (!searching) {
      return { groups: groupConversations(rows, pinnedIds, now), highlights: new Map<string, Array<[number, number]>>() };
    }
    const hits = searchConversations(rows, query);
    const map = new Map<string, Array<[number, number]>>();
    for (const h of hits) map.set(h.item.id, h.ranges);
    const label = `${hits.length} ${hits.length === 1 ? "result" : "results"}`;
    return {
      groups: hits.length ? [{ label, items: hits.map((h) => h.item) }] : [],
      highlights: map,
    };
  }, [rows, pinnedIds, now, query, searching]);

  const total = rows.length;

  let body: React.ReactNode;

  if (loading && conversations.length === 0) {
    body = <LoadingRows />;
  } else if (error) {
    body = (
      <CenteredState icon={AlertCircle} title="Could not load history" body={error}>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border/70 bg-card px-3 py-1.5 text-[0.75rem] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-white/10 dark:bg-white/[0.06]"
          >
            <RotateCcw className="size-3.5" />
            Try again
          </button>
        )}
      </CenteredState>
    );
  } else if (groups.length === 0) {
    body = searching ? (
      <CenteredState
        icon={SearchX}
        title="No matches"
        body={`Nothing in the last ${HISTORY_WINDOW_DAYS} days matches "${query.trim()}".`}
      />
    ) : (
      <CenteredState
        icon={Clock}
        title="No conversations yet"
        body={`Chats you have in the last ${HISTORY_WINDOW_DAYS} days will show up here.`}
      />
    );
  } else {
    body = (
      <motion.div
        key={searching ? "results" : "groups"}
        variants={reduceMotion ? undefined : { hidden: {}, show: { transition: { staggerChildren: 0.035 } } }}
        initial={reduceMotion ? undefined : "hidden"}
        animate={reduceMotion ? undefined : "show"}
        className="px-2.5 pb-6 pt-3 sm:px-3"
      >
        {groups.map((group) => (
          <section key={group.label} className="mb-4 last:mb-0">
            <h3 className="sticky top-0 z-10 flex items-center gap-1 bg-white/85 px-1 pb-1.5 pt-1 text-[0.6875rem] font-semibold tracking-wider text-muted-foreground backdrop-blur-sm dark:bg-[#171430]/85">
              {group.label === "Pinned" && <Pin className="size-3 fill-current text-primary" />}
              {group.label}
            </h3>
            <ul className="space-y-1.5">
              {group.items.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  now={now}
                  isActive={c.id === activeId}
                  isPinned={pinnedIds.includes(c.id)}
                  ranges={highlights.get(c.id) ?? []}
                  onSelect={onSelect}
                  onTogglePin={onTogglePin}
                  onDelete={onDelete}
                />
              ))}
            </ul>
          </section>
        ))}
      </motion.div>
    );
  }

  const toolbar = (
    <>
      <SearchBar value={query} onChange={setQuery} />
      {actionError && (
        <div
          role="alert"
          className="flex shrink-0 items-start gap-2 border-b border-red-400/30 bg-red-500/[0.06] px-3 py-2"
        >
          <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-red-500" />
          <span className="min-w-0 flex-1 text-[0.75rem] leading-snug text-foreground">
            {actionError}
          </span>
          {onDismissActionError && (
            <button
              type="button"
              onClick={onDismissActionError}
              aria-label="Dismiss error"
              className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      )}
    </>
  );

  return (
    <PanelShell count={total} onClose={onClose} toolbar={toolbar}>
      {body}
    </PanelShell>
  );
}
